import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  TextInput,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  TextInputProps,
} from 'react-native';
import Avatar from '@/components/Avatar';
import { apiService } from '@/services/apiService';

export interface MentionUser {
  id: string;
  username: string;
  full_name: string;
  avatar_url: string | null;
}

interface MentionInputProps extends Omit<TextInputProps, 'onChangeText'> {
  value: string;
  onChangeText: (text: string) => void;
  onMentionsChange?: (userIds: string[]) => void;
  inputRef?: React.RefObject<TextInput>;
  dropdownPosition?: 'above' | 'below';
  externalDropdown?: boolean;
  wrapperStyle?: import('react-native').ViewStyle;
  onSuggestionsChange?: (
    suggestions: MentionUser[],
    isSearching: boolean,
    onSelectUser: (user: MentionUser) => void
  ) => void;
}

const MENTION_REGEX = /@([a-zA-Z0-9._]*)$/;
const DEBOUNCE_MS = 300;

export default function MentionInput({
  value,
  onChangeText,
  onMentionsChange,
  inputRef: externalRef,
  style,
  dropdownPosition = 'above',
  externalDropdown = false,
  wrapperStyle,
  onSuggestionsChange,
  ...rest
}: MentionInputProps) {
  const internalRef = useRef<TextInput>(null);
  const inputRef = externalRef || internalRef;

  const [suggestions, setSuggestions] = useState<MentionUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeMention, setActiveMention] = useState<{
    start: number;
    query: string;
  } | null>(null);
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cursorPos = useRef<number>(0);

  const searchUsers = useCallback(async (query: string) => {
    if (query.length === 0) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await apiService.get<{ success: boolean; users: MentionUser[] }>(
        `/api/community/users/search?q=${encodeURIComponent(query)}&limit=6`
      );
      if (response.success && response.data?.users) {
        setSuggestions(response.data.users);
      } else {
        setSuggestions([]);
      }
    } catch {
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleChangeText = useCallback(
    (text: string) => {
      onChangeText(text);

      const cursor = cursorPos.current;
      const textUpToCursor = text.slice(0, cursor);
      const match = MENTION_REGEX.exec(textUpToCursor);

      if (match) {
        const query = match[1];
        const mentionStart = cursor - match[0].length;
        setActiveMention({ start: mentionStart, query });

        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => searchUsers(query), DEBOUNCE_MS);
      } else {
        setActiveMention(null);
        setSuggestions([]);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
      }
    },
    [onChangeText, searchUsers]
  );

  const handleSelectionChange = useCallback(
    (event: { nativeEvent: { selection: { start: number; end: number } } }) => {
      cursorPos.current = event.nativeEvent.selection.end;
    },
    []
  );

  const handleSelectUser = useCallback(
    (user: MentionUser) => {
      if (!activeMention) return;

      const before = value.slice(0, activeMention.start);
      const after = value.slice(activeMention.start + activeMention.query.length + 1);
      const inserted = `@${user.username} `;
      const newText = before + inserted + after;

      onChangeText(newText);
      setActiveMention(null);
      setSuggestions([]);

      const updatedIds = Array.from(new Set([...mentionedUserIds, user.id]));
      setMentionedUserIds(updatedIds);
      onMentionsChange?.(updatedIds);

      setTimeout(() => {
        const newCursor = before.length + inserted.length;
        inputRef.current?.setNativeProps({ selection: { start: newCursor, end: newCursor } });
        inputRef.current?.focus();
      }, 50);
    },
    [activeMention, value, onChangeText, mentionedUserIds, onMentionsChange, inputRef]
  );

  const selectUserRef = useRef(handleSelectUser);
  selectUserRef.current = handleSelectUser;

  const stableSelectUser = useCallback((user: MentionUser) => {
    selectUserRef.current(user);
  }, []);

  useEffect(() => {
    if (onSuggestionsChange) {
      onSuggestionsChange(suggestions, isSearching, stableSelectUser);
    }
  }, [suggestions, isSearching]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const showDropdown = !externalDropdown && activeMention !== null && (isSearching || suggestions.length > 0);

  const dropdown = showDropdown ? (
    <View style={styles.dropdown}>
      {isSearching && suggestions.length === 0 ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color="#0fa971" />
        </View>
      ) : (
        suggestions.map((user) => (
          <TouchableOpacity
            key={user.id}
            style={styles.suggestionRow}
            onPressIn={() => handleSelectUser(user)}
            activeOpacity={0.7}
          >
            <Avatar
              uri={user.avatar_url}
              size={32}
              loading={false}
              showInitials
              initials={user.full_name || user.username}
            />
            <View style={styles.suggestionText}>
              <Text style={styles.suggestionUsername}>@{user.username}</Text>
              {user.full_name ? (
                <Text style={styles.suggestionName}>{user.full_name}</Text>
              ) : null}
            </View>
          </TouchableOpacity>
        ))
      )}
    </View>
  ) : null;

  return (
    <View style={[styles.wrapper, wrapperStyle]}>
      {dropdownPosition === 'above' && dropdown}

      <TextInput
        ref={inputRef}
        value={value}
        onChangeText={handleChangeText}
        onSelectionChange={handleSelectionChange}
        style={style}
        {...rest}
        selectionColor="#6B9EC2"
      />

      {dropdownPosition === 'below' && dropdown}
    </View>
  );
}

export function MentionSuggestionList({
  suggestions,
  isSearching,
  onSelect,
}: {
  suggestions: MentionUser[];
  isSearching: boolean;
  onSelect: (user: MentionUser) => void;
}) {
  if (!isSearching && suggestions.length === 0) return null;

  return (
    <View style={styles.dropdown}>
      {isSearching && suggestions.length === 0 ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color="#0fa971" />
        </View>
      ) : (
        suggestions.map((user) => (
          <TouchableOpacity
            key={user.id}
            style={styles.suggestionRow}
            onPressIn={() => onSelect(user)}
            activeOpacity={0.7}
          >
            <Avatar
              uri={user.avatar_url}
              size={32}
              loading={false}
              showInitials
              initials={user.full_name || user.username}
            />
            <View style={styles.suggestionText}>
              <Text style={styles.suggestionUsername}>@{user.username}</Text>
              {user.full_name ? (
                <Text style={styles.suggestionName}>{user.full_name}</Text>
              ) : null}
            </View>
          </TouchableOpacity>
        ))
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  dropdown: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    marginBottom: 6,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 4,
  },
  loadingRow: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  suggestionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  suggestionText: {
    flex: 1,
  },
  suggestionUsername: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    fontWeight: 'bold',
    color: '#6B9EC2',
  },
  suggestionName: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    fontWeight: '500',
    color: '#6B7280',
    marginTop: 1,
  },
});
